export interface Stats {
  jobs: number;
  new_jobs: number;
  outreach_drafts: number;
  outreach_sent: number;
  contacts: number;
}

export interface Job {
  id: number;
  source: string;
  source_url: string;
  external_id?: string;
  search_name?: string;
  title: string;
  company: string;
  company_domain?: string;
  location: string;
  salary_min?: number | null;
  salary_max?: number | null;
  seniority: string;
  skills: string[];
  description?: string;
  posted_at?: string;
  score: number;
  reasons: string[];
  status: string;
  created_at: string;
}

export interface Search {
  name: string;
  role: string;
  keywords: string[];
  skills: string[];
  location?: string;
  seniority: string[];
  min_score: number;
  max_days_old: number;
}

export interface Outreach {
  id: number;
  job_id: number | null;
  contact_id: number | null;
  search_name: string | null;
  subject: string;
  body: string;
  status: string;
  variant: number;
  created_at: string;
}

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "";

function token(): string {
  return (
    localStorage.getItem("jh_token") ??
    (import.meta.env.VITE_API_TOKEN as string | undefined) ??
    ""
  );
}

export function setToken(value: string) {
  if (value) localStorage.setItem("jh_token", value);
  else localStorage.removeItem("jh_token");
}

export function apiBase(): string {
  return API_BASE;
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const t = token();
  if (t) headers.Authorization = `Bearer ${t}`;
  const resp = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`API ${resp.status}: ${text || resp.statusText}`);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

export const api = {
  health: () => req<{ ok: boolean }>("/api/health"),
  stats: () => req<Stats>("/api/stats"),
  settings: () => req<Record<string, string>>("/api/settings"),
  saveSettings: (s: Record<string, string>) => req("/api/settings", { method: "PUT", body: JSON.stringify(s) }),
  searches: () => req<Search[]>("/api/searches"),
  saveSearch: (s: Search) => req("/api/searches", { method: "POST", body: JSON.stringify(s) }),
  deleteSearch: (name: string) => req(`/api/searches/${encodeURIComponent(name)}`, { method: "DELETE" }),
  jobs: (status?: string, limit = 100) => {
    const q = new URLSearchParams({ limit: String(limit) });
    if (status) q.set("status", status);
    return req<Job[]>(`/api/jobs?${q}`);
  },
  setJobStatus: (id: number, status: string) => req(`/api/jobs/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }),
  outreach: (status?: string) => req<Outreach[]>(`/api/outreach${status ? `?status=${status}` : ""}`),
  createOutreach: (o: { job_id: number | null; contact_id: number | null; search_name: string | null; subject: string; body: string }) =>
    req<{ id: number }>("/api/outreach", { method: "POST", body: JSON.stringify(o) }),
  updateOutreach: (id: number, patch: Partial<Outreach>) =>
    req(`/api/outreach/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  suppression: () => req<string[]>("/api/suppression"),
  addSuppression: (value: string) => req("/api/suppression", { method: "POST", body: JSON.stringify({ value }) }),
  llmGenerate: (payload: unknown) => req("/api/llm/generate", { method: "POST", body: JSON.stringify(payload) }),
  fetchModels: (payload: { provider: string; api_key?: string; base_url?: string }) =>
    req<{ models: string[]; error?: string }>("/api/models/fetch", { method: "POST", body: JSON.stringify(payload) }),
};
